import {useCallback, useMemo, useState} from 'react';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import {getRackCatalogList} from '@/services/rewardCatalog';
import {formatDateToMMDDYYYY} from '@/utility/dateHelper';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];
const RACK_CATALOG_QUERY_KEY = 'rackCatalog';

export const useRackCatalog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const {data, isLoading} = useQuery({
    queryKey: [RACK_CATALOG_QUERY_KEY],
    queryFn: getRackCatalogList,
    placeholderData: keepPreviousData
  });

  const allRacks = useMemo(() => data?.data || [], [data]);

  const filteredRacks = useMemo(() => {
    if (!searchTerm) return allRacks;
    const term = searchTerm.toLowerCase();
    return allRacks.filter(
      (rack) =>
        rack.displayName?.toLowerCase().includes(term) ||
        rack.rackId?.toLowerCase().includes(term)
    );
  }, [allRacks, searchTerm]);

  const totalRacks = filteredRacks.length;

  const lastUpdated = useMemo(() => {
    if (allRacks.length === 0) return '';
    const dates = allRacks
      .map((r) => r.updatedAt || r.createdAt)
      .filter(Boolean)
      .map((d) => new Date(d).getTime());
    if (dates.length === 0) return '';
    return formatDateToMMDDYYYY(new Date(Math.max(...dates)).toISOString());
  }, [allRacks]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalRacks / pageSize));
  }, [totalRacks, pageSize]);

  const rackList = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredRacks.slice(start, start + pageSize);
  }, [filteredRacks, pageIndex, pageSize]);

  const isEmpty = !isLoading && allRacks.length === 0;

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    setPageIndex(0);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPageIndex(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPageIndex(0);
  }, []);

  return {
    rackList,
    totalRacks,
    lastUpdated,
    isLoading,
    isEmpty,
    searchTerm,
    handleSearchChange,
    pageIndex,
    pageSize,
    totalPages,
    handlePageChange,
    handlePageSizeChange,
    rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS
  };
};
